import {Link} from "react-router-dom";
import {ChevronRight} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Loader} from "@/src/modules/core/components";
import {useCitations} from "@/src/modules/citations/hooks/use-citations";

import {ResidentHomeCitationsPreviewRow} from "./resident-home-citations-preview-row";

const EXPAND_LIMIT = 10;

type ResidentHomeCitationsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: Record<string, unknown>;
  householdId: number | null;
  canShowDetail: boolean;
  onSelectDetail: (id: number) => void;
};

export function ResidentHomeCitationsSheet({
  open,
  onOpenChange,
  filter,
  householdId,
  canShowDetail,
  onSelectDetail,
}: ResidentHomeCitationsSheetProps) {
  const {citations, totalRecords, isPending} = useCitations({
    rows: EXPAND_LIMIT,
    page: 1,
    filters: filter,
    sort: "-citation_date",
    enabled: open && Boolean(householdId),
  });

  const handleSelect = (id: number) => {
    if (!canShowDetail) return;
    onSelectDetail(id);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex min-h-0 flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-border border-b">
          <SheetTitle>Citations</SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {!householdId ? (
            <p className="text-muted-foreground text-sm">No household linked.</p>
          ) : isPending ? (
            <Loader className="min-h-48" />
          ) : citations.length === 0 ? (
            <p className="text-muted-foreground text-sm">No citations yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {citations.map((row) => (
                <ResidentHomeCitationsPreviewRow
                  key={row.citation_id}
                  citation={row}
                  onSelect={handleSelect}
                />
              ))}
              {totalRecords > EXPAND_LIMIT ? (
                <p className="text-muted-foreground pt-2 text-center text-xs">
                  Showing {EXPAND_LIMIT} of {totalRecords} — open the full list for more.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <SheetFooter className="border-border border-t px-4 py-3">
          <Button
            render={<Link to="/citations" onClick={() => onOpenChange(false)} />}
            variant="outline"
            className="w-full"
          >
            View all on citations page
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
